import React, { useState, useRef, useEffect } from 'react';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface EditableTitleProps {
  title: string;
  description: string;
  slug: string; // Still kept for compatibility but not rendered
  isActive?: boolean;
  meetingType?: '1-on-1' | 'event';
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSlugChange?: (value: string) => void; // Optional - not used for one-time links
  showSlugField?: boolean; // Whether to show slug field (currently slug is not rendered)
  disabled?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  user?: {
    customUrl?: string;
    username?: string;
    email?: string;
  };
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  description,
  slug,
  isActive = true,
  meetingType = '1-on-1',
  onTitleChange,
  onDescriptionChange,
  onSlugChange,
  disabled = false,
  onClose,
  showCloseButton = false,
  user,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const [tempDescription, setTempDescription] = useState(description);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  useEffect(() => {
    setTempDescription(description);
  }, [description]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (isEditingDescription && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus();
      descriptionTextareaRef.current.select();
    }
  }, [isEditingDescription]);

  const handleTitleSave = () => {
    onTitleChange(tempTitle);
    setIsEditingTitle(false);
  };

  const handleDescriptionSave = () => {
    onDescriptionChange(tempDescription);
    setIsEditingDescription(false);
  };

  const handleTitleCancel = () => {
    setTempTitle(title);
    setIsEditingTitle(false);
  };

  const handleDescriptionCancel = () => {
    setTempDescription(description);
    setIsEditingDescription(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      handleDescriptionCancel();
    }
    // Allow Enter for new lines in textarea
  };

  return (
    <div className="space-y-4 w-full max-w-full min-w-0">
      {/* Title with Close Button */}
      <div className="group relative">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            {isEditingTitle ? (
              <div className="border-b-2 border-primary-500">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={handleTitleSave}
                  className="w-full text-2xl font-semibold text-gray-900 focus:outline-none bg-transparent py-1"
                  placeholder="Enter meeting title..."
                  disabled={disabled}
                  style={{ paddingLeft: 0, paddingRight: 0, border: 'none', boxShadow: 'none', outline: 'none' }}
                />
              </div>
            ) : (
              <div
                className={`cursor-text ${!disabled ? 'hover:bg-gray-50' : ''} py-1 transition-colors duration-200`}
                onClick={() => !disabled && setIsEditingTitle(true)}
                style={{ outline: 'none', userSelect: 'none' }}
                onFocus={(e) => e.preventDefault()}
              >
                <h2 className="text-2xl font-semibold text-gray-900 break-words overflow-wrap-anywhere">
                  {title || <span className="text-gray-400">Click to add title...</span>}
                </h2>
              </div>
            )}
          </div>
          {showCloseButton && onClose && (
            <button
              type="button"
              className="flex-shrink-0 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Description - closer to title */}
      <div className="group relative pb-2">
        {isEditingDescription ? (
          <div className="border-b-2 border-primary-500">
            <textarea
              ref={descriptionTextareaRef}
              value={tempDescription}
              onChange={(e) => setTempDescription(e.target.value)}
              onKeyDown={handleDescriptionKeyDown}
              onBlur={handleDescriptionSave}
              className="w-full text-base text-gray-600 focus:outline-none bg-transparent resize-none overflow-hidden py-1"
              placeholder="Enter meeting description..."
              rows={Math.max(2, tempDescription.split('\n').length)}
              disabled={disabled}
              style={{
                minHeight: '60px',
                height: 'auto',
                paddingLeft: 0,
                paddingRight: 0,
                border: 'none',
                boxShadow: 'none',
                outline: 'none'
              }}
            />
          </div>
        ) : (
          <div
            className={`cursor-text ${!disabled ? 'hover:bg-gray-50' : ''} py-1 transition-colors duration-200`}
            onClick={() => !disabled && setIsEditingDescription(true)}
            style={{
              minHeight: '60px',
              outline: 'none',
              userSelect: 'none'
            }}
            onFocus={(e) => e.preventDefault()}
          >
            <p className="text-base text-gray-600 whitespace-pre-wrap break-words overflow-wrap-anywhere">
              {description || <span className="text-gray-400 italic">(optional) Tell your visitors what this meeting is about</span>}
            </p>
            {!disabled && (
              <PencilIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-0" />
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default EditableTitle;